import { BRAILLE_BIT, BRAILLE_COLS, BRAILLE_ROWS, isBraille, maskFromBraille } from '../unicode/braille';
import { edgesOf } from '../unicode/boxdrawing';

/**
 * A coverage atlas: how much ink each candidate character puts in each part of
 * a cell.
 *
 * Block, box-drawing and Braille glyphs are computed analytically from the
 * geometry they are defined by — a Braille glyph *is* its dot pattern, a box
 * glyph *is* its four edges — which is both exact and available without a font.
 * Letters have no such definition, so they are rasterised from the actual font
 * when a canvas is available, and simply left out when it is not.
 */

/**
 * Feature resolution per cell.
 *
 * Tall, because terminal cells are tall, and fine enough to separate the two
 * strokes of a double box line and to resolve Braille's 2x4 dot grid — at 6x12
 * both blurred into their single-stroke and block equivalents.
 */
export const GLYPH_W = 8;
export const GLYPH_H = 16;

export interface GlyphSample {
  char: string;
  /** GLYPH_W * GLYPH_H coverage values in 0..1, row-major. */
  coverage: Float32Array;
  /** Mean coverage, used to skip obviously wrong candidates cheaply. */
  mean: number;
  /** Higher = more visually complex; used to break ties toward simpler glyphs. */
  complexity: number;
}

export interface GlyphAtlas {
  width: number;
  height: number;
  glyphs: GlyphSample[];
}

export type GlyphFamily = 'blocks' | 'shades' | 'box' | 'braille' | 'letters' | 'punctuation';

export const FAMILY_LABELS: Record<GlyphFamily, string> = {
  blocks: 'Block elements',
  shades: 'Shading',
  box: 'Box drawing',
  braille: 'Braille',
  letters: 'Letters & digits',
  punctuation: 'Punctuation',
};

const BLOCK_CHARS = [
  ' ', '█', '▀', '▄', '▌', '▐',
  '▖', '▗', '▘', '▙', '▚', '▛',
  '▜', '▝', '▞', '▟',
  '▁', '▂', '▃', '▅', '▆', '▇',
  '▉', '▊', '▋', '▍', '▎', '▏',
  '▔', '▕',
];

const SHADE_CHARS = ['░', '▒', '▓'];

const LETTER_CHARS = [
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  ...'abcdefghijklmnopqrstuvwxyz',
  ...'0123456789',
];

const PUNCTUATION_CHARS = [...'.,:;!?\'"`^~*+-=_/\\|<>()[]{}#$%&@'];

function boxChars(): string[] {
  const out: string[] = [];
  for (let cp = 0x2500; cp <= 0x257f; cp++) out.push(String.fromCodePoint(cp));
  return out;
}

function brailleChars(): string[] {
  const out: string[] = [];
  // The full 256 patterns would dominate the atlas; every 1 in 3 still covers
  // the useful density range while keeping matching fast.
  for (let mask = 1; mask < 256; mask += 1) out.push(String.fromCodePoint(0x2800 + mask));
  return out;
}

export function familyChars(family: GlyphFamily): string[] {
  switch (family) {
    case 'blocks':
      return BLOCK_CHARS;
    case 'shades':
      return SHADE_CHARS;
    case 'box':
      return boxChars();
    case 'braille':
      return brailleChars();
    case 'letters':
      return LETTER_CHARS;
    case 'punctuation':
      return PUNCTUATION_CHARS;
  }
}

/* ------------------------------------------------------------------ *
 * Analytic coverage
 * ------------------------------------------------------------------ */

function blank(): Float32Array {
  return new Float32Array(GLYPH_W * GLYPH_H);
}

/** Fills an axis-aligned rectangle in normalised (0..1) coordinates. */
function fillRect(
  cov: Float32Array,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  value = 1,
): void {
  for (let y = 0; y < GLYPH_H; y++) {
    const cy0 = y / GLYPH_H;
    const cy1 = (y + 1) / GLYPH_H;
    const oy = Math.max(0, Math.min(cy1, y1) - Math.max(cy0, y0)) * GLYPH_H;
    if (oy <= 0) continue;
    for (let x = 0; x < GLYPH_W; x++) {
      const cx0 = x / GLYPH_W;
      const cx1 = (x + 1) / GLYPH_W;
      const ox = Math.max(0, Math.min(cx1, x1) - Math.max(cx0, x0)) * GLYPH_W;
      if (ox <= 0) continue;
      const i = y * GLYPH_W + x;
      cov[i] = Math.min(1, cov[i] + ox * oy * value);
    }
  }
}

/** Eighth-block fractions for the vertical and horizontal block ramps. */
const LOWER_EIGHTHS: Record<number, number> = {
  0x2581: 1 / 8, 0x2582: 2 / 8, 0x2583: 3 / 8, 0x2584: 4 / 8,
  0x2585: 5 / 8, 0x2586: 6 / 8, 0x2587: 7 / 8, 0x2588: 1,
};
const LEFT_EIGHTHS: Record<number, number> = {
  0x258f: 1 / 8, 0x258e: 2 / 8, 0x258d: 3 / 8, 0x258c: 4 / 8,
  0x258b: 5 / 8, 0x258a: 6 / 8, 0x2589: 7 / 8,
};
const QUADRANTS: Record<number, [boolean, boolean, boolean, boolean]> = {
  // [top-left, top-right, bottom-left, bottom-right]
  0x2596: [false, false, true, false],
  0x2597: [false, false, false, true],
  0x2598: [true, false, false, false],
  0x2599: [true, false, true, true],
  0x259a: [true, false, false, true],
  0x259b: [true, true, true, false],
  0x259c: [true, true, false, true],
  0x259d: [false, true, false, false],
  0x259e: [false, true, true, false],
  0x259f: [false, true, true, true],
};

/** Line thickness for each box-drawing weight, as a fraction of the cell. */
const EDGE_THICKNESS: Record<number, number> = { 1: 0.14, 2: 0.26, 3: 0.34 };

export function analyticCoverage(char: string): Float32Array | null {
  const cov = blank();
  const cp = char.codePointAt(0) ?? 0;

  if (char === ' ') return cov;
  if (cp === 0x2588) {
    fillRect(cov, 0, 0, 1, 1);
    return cov;
  }
  if (LOWER_EIGHTHS[cp] !== undefined) {
    fillRect(cov, 0, 1 - LOWER_EIGHTHS[cp], 1, 1);
    return cov;
  }
  if (cp === 0x2594) {
    fillRect(cov, 0, 0, 1, 1 / 8);
    return cov;
  }
  if (cp === 0x2580) {
    fillRect(cov, 0, 0, 1, 0.5);
    return cov;
  }
  if (LEFT_EIGHTHS[cp] !== undefined) {
    fillRect(cov, 0, 0, LEFT_EIGHTHS[cp], 1);
    return cov;
  }
  if (cp === 0x2590) {
    fillRect(cov, 0.5, 0, 1, 1);
    return cov;
  }
  if (cp === 0x2595) {
    fillRect(cov, 7 / 8, 0, 1, 1);
    return cov;
  }
  if (QUADRANTS[cp]) {
    const [tl, tr, bl, br] = QUADRANTS[cp];
    if (tl) fillRect(cov, 0, 0, 0.5, 0.5);
    if (tr) fillRect(cov, 0.5, 0, 1, 0.5);
    if (bl) fillRect(cov, 0, 0.5, 0.5, 1);
    if (br) fillRect(cov, 0.5, 0.5, 1, 1);
    return cov;
  }
  // Shades are uniform partial coverage, which is exactly how they read at
  // a distance.
  if (cp === 0x2591) {
    cov.fill(0.25);
    return cov;
  }
  if (cp === 0x2592) {
    cov.fill(0.5);
    return cov;
  }
  if (cp === 0x2593) {
    cov.fill(0.75);
    return cov;
  }

  if (isBraille(char)) {
    const mask = maskFromBraille(char);
    for (let r = 0; r < BRAILLE_ROWS; r++) {
      for (let c = 0; c < BRAILLE_COLS; c++) {
        if ((mask & BRAILLE_BIT[r][c]) === 0) continue;
        // Dots sit inside their sub-cell with a little padding, like the font.
        const x0 = c / BRAILLE_COLS + 0.06;
        const x1 = (c + 1) / BRAILLE_COLS - 0.06;
        const y0 = r / BRAILLE_ROWS + 0.02;
        const y1 = (r + 1) / BRAILLE_ROWS - 0.02;
        fillRect(cov, x0, y0, x1, y1);
      }
    }
    return cov;
  }

  const edges = edgesOf(char);
  if (edges) {
    const weights = [edges.n, edges.e, edges.s, edges.w].filter((w) => w > 0);
    const t = EDGE_THICKNESS[Math.max(...weights, 1)] ?? 0.14;
    const half = t / 2;
    // Double lines are two thin strokes rather than one thick one.
    const strokeH = (x0: number, x1: number, weight: number) => {
      if (weight === 3) {
        fillRect(cov, x0, 0.5 - half - 0.06, x1, 0.5 - half + 0.02);
        fillRect(cov, x0, 0.5 + half - 0.02, x1, 0.5 + half + 0.06);
      } else {
        fillRect(cov, x0, 0.5 - half, x1, 0.5 + half);
      }
    };
    const strokeV = (y0: number, y1: number, weight: number) => {
      if (weight === 3) {
        fillRect(cov, 0.5 - half - 0.08, y0, 0.5 - half + 0.02, y1);
        fillRect(cov, 0.5 + half - 0.02, y0, 0.5 + half + 0.08, y1);
      } else {
        fillRect(cov, 0.5 - half, y0, 0.5 + half, y1);
      }
    };
    if (edges.w) strokeH(0, 0.5 + half, edges.w);
    if (edges.e) strokeH(0.5 - half, 1, edges.e);
    if (edges.n) strokeV(0, 0.5 + half, edges.n);
    if (edges.s) strokeV(0.5 - half, 1, edges.s);
    return cov;
  }

  return null;
}

/* ------------------------------------------------------------------ *
 * Font rasterisation (browser only)
 * ------------------------------------------------------------------ */

export type Rasterizer = (char: string) => Float32Array | null;

/**
 * Rasterises glyphs with the browser's own font.
 *
 * Supersampled and box-filtered down to the feature grid, so a thin stroke
 * contributes partial coverage instead of disappearing.
 */
export function canvasRasterizer(fontFamily = 'monospace', superSample = 4): Rasterizer | null {
  if (typeof document === 'undefined') return null;
  const w = GLYPH_W * superSample;
  const h = GLYPH_H * superSample;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  // Size the font so a capital letter fills most of the cell height.
  const fontSize = h * 0.82;
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  return (char: string): Float32Array | null => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#fff';
    ctx.fillText(char, w / 2, h / 2 + h * 0.02);
    const data = ctx.getImageData(0, 0, w, h).data;
    const cov = blank();
    let ink = 0;
    for (let y = 0; y < h; y++) {
      const gy = Math.floor((y / h) * GLYPH_H);
      for (let x = 0; x < w; x++) {
        const gx = Math.floor((x / w) * GLYPH_W);
        const alpha = data[(y * w + x) * 4 + 3] / 255;
        if (alpha > 0) ink += alpha;
        cov[gy * GLYPH_W + gx] += alpha;
      }
    }
    if (ink === 0 && char !== ' ') return null;
    const per = superSample * superSample;
    for (let i = 0; i < cov.length; i++) cov[i] = Math.min(1, cov[i] / per);
    return cov;
  };
}

function statsOf(coverage: Float32Array): { mean: number; complexity: number } {
  let sum = 0;
  for (const v of coverage) sum += v;
  const mean = sum / coverage.length;
  // Complexity is edge energy: how much the glyph changes across the cell.
  let edges = 0;
  for (let y = 0; y < GLYPH_H; y++) {
    for (let x = 0; x < GLYPH_W; x++) {
      const i = y * GLYPH_W + x;
      if (x + 1 < GLYPH_W) edges += Math.abs(coverage[i] - coverage[i + 1]);
      if (y + 1 < GLYPH_H) edges += Math.abs(coverage[i] - coverage[i + GLYPH_W]);
    }
  }
  return { mean, complexity: edges / coverage.length };
}

export interface AtlasOptions {
  families: readonly GlyphFamily[];
  /** Used for letters and punctuation; analytic families ignore it. */
  rasterizer?: Rasterizer | null;
}

/**
 * Builds the atlas for the selected families.
 *
 * Analytic coverage is preferred wherever it exists — it does not depend on
 * which font the browser happened to pick — and the rasteriser fills in the
 * rest. Families whose glyphs cannot be measured are skipped rather than
 * guessed at.
 */
export function buildGlyphAtlas(options: AtlasOptions): GlyphAtlas {
  const glyphs: GlyphSample[] = [];
  const seen = new Set<string>();

  for (const family of options.families) {
    for (const char of familyChars(family)) {
      if (seen.has(char)) continue;
      const coverage = analyticCoverage(char) ?? options.rasterizer?.(char) ?? null;
      if (!coverage) continue;
      seen.add(char);
      const { mean, complexity } = statsOf(coverage);
      glyphs.push({ char, coverage, mean, complexity });
    }
  }

  // Cheap pre-filtering during matching relies on this ordering.
  glyphs.sort((a, b) => a.mean - b.mean);
  return { width: GLYPH_W, height: GLYPH_H, glyphs };
}
